<template>
    <CustomDialog
        v-model="isVisible"
        :defaultTitle="$t(`${type}.edit_title`, { code: item.code })"
        width="500px"
        :isInPopup="true"
        @confirm="handleSave"
    >
        <template #content>
            <div class="ppv2-space-y-6">
                <!-- Expiration Date -->
                <BaseDateInput v-model="form.date_end" :label="$t(`${type}.date_end`)" :errorText="errors.date_end" />
                <!-- Blocked Switch -->
                <div class="ppv2-flex ppv2-items-center ppv2-gap-3">
                    <BaseSwitch
                        :label="form.is_blocked ? $t(`${type}.status_blocked`) : $t(`${type}.status_active`)"
                        v-model="form.is_blocked"
                        :boxed="true"
                        :variant="form.is_blocked ? 'gray' : 'success'"
                        :customLabelStyle="`font-size: ${normalizeSize(15, true)}`"
                        customLabelClass="poppinsLight ppv2-text-black"
                    />
                </div>
            </div>
        </template>

        <template #actions>
            <BaseButton variant="outline" @click="close" :disabled="loading">
                {{ $t('common.cancel') }}
            </BaseButton>
            <BaseButton variant="primary" @click="handleSave" :loading="loading" :disabled="loading">
                {{ $t('common.save') }}
            </BaseButton>
        </template>
    </CustomDialog>
</template>

<script>
import CustomDialog from '../../../Shared/CustomDialog.vue'
import CustomInput from '../../../Inputs/CustomInput.vue'
import BaseSwitch from '../../../Shared/BaseSwitch.vue'
import BaseDateInput from '../../../Shared/BaseDateInput.vue'
import BaseButton from '../../../Shared/BaseButton.vue'
import BaseLabel from '../../../Shared/BaseLabel.vue'
import normalizeSize from '../../../../utils/normalizeSize'
import axios from 'axios'

export default {
    name: 'CustomerVoucherEditModal',
    components: {
        CustomDialog,
        CustomInput,
        BaseDateInput,
        BaseButton,
        BaseSwitch,
        BaseLabel,
    },
    props: {
        value: {
            type: Boolean,
            default: false,
        },
        item: {
            type: Object,
            required: true,
        },
        type: {
            type: String,
            default: 'voucher', // or 'billetNonDate'
            validator: (value) => ['voucher', 'billetNonDate'].includes(value),
        },
        token: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            loading: false,
            form: {
                date_end: '',
                is_blocked: false,
            },
            errors: {},
        }
    },
    computed: {
        isVisible: {
            get() {
                return this.value
            },
            set(val) {
                this.$emit('input', val)
            },
        },
    },
    watch: {
        value(val) {
            if (val && this.item) {
                this.initForm()
            }
        },
        item: {
            immediate: true,
            handler() {
                if (this.value) {
                    this.initForm()
                }
            },
        },
    },
    methods: {
        normalizeSize,
        initForm() {
            this.form = {
                is_blocked: !!this.item.is_blocked,
                date_end: this.formatDateForInput(this.item.date_end),
            }
            this.errors = {}
        },
        formatDateForInput(date) {
            if (!date) return ''
            // Handle if it's already YYYY-MM-DD
            if (/^\d{4}-\d{2}-\d{2}/.test(date)) {
                return date.split(' ')[0]
            }
            // Handle DD/MM/YYYY
            const parts = date.split(' ')[0].split('/')
            if (parts.length === 3) {
                return `${parts[2]}-${parts[1]}-${parts[0]}`
            }
            return date
        },
        async handleSave() {
            this.loading = true
            this.errors = {}

            try {
                const response = await axios.post(`/api/v3/${this.type}/update/${this.item.id}`, this.form, {
                    headers: { Authorization: `Bearer ${this.token}` },
                })
                this.$toast?.success?.(response.data.message || this.$t('voucher.update_success'))
                this.$emit('updated', response.data.data)
                this.close()
            } catch (error) {
                this.$toast.error(error.response.data.message)
                if (error.response && error.response.status === 422) {
                    this.errors = error.response.data.errors
                }
            } finally {
                this.loading = false
            }
        },
        close() {
            this.isVisible = false
        },
    },
}
</script>

<style scoped>
.ppv2-space-y-6 > * + * {
    margin-top: 1.5rem;
}
</style>
